import {
  Button,
  Card,
  Spinner,
  CardHeader,
  InputGroup,
  CardBody,
} from "reactstrap";
import apiService from "services/api";
import { useState } from "react";
import { firstDay, lastDay } from "utils/dateHelper";
import { useQuery } from "react-query";
import Select from "react-select";
import { fileResponseDownloader } from "utils/fileResponseDownloader";
import { ReportSelectionCard } from "components";
import moment from "moment";
import { months } from "./constants";

const initialState = {
  date_from: firstDay(undefined, "absolute_month"),
  date_to: lastDay(undefined, "absolute_month"),
  staff: undefined,
  grouped: false,
};

const apiCallMapper = {
  leave: apiService.report.leave,
  ot: apiService.report.ot,
  transaction: apiService.report.transaction,
  addPointRecord: apiService.report.addPointRecord,
  studentSummary: apiService.report.studentSummaryV2,
  partTimeWageReport: apiService.report.partTimeWageReport,
  weeklyTeachingHours: apiService.report.weeklyTeachingHours,
  lessonRecordSummaryReport: apiService.report.lessonRecordSummaryReport,
  staffAttendanceReport: apiService.report.staffAttendanceReport,
  ludaAttendanceReport: apiService.report.ludaAttendanceReport,
  geniusAttendanceReport: apiService.report.geniusAttendanceReport,
  teachingHoursSummaryReport: apiService.report.teachingHoursSummaryReport,
  coursePurchaseRecordReport: apiService.report.coursePurchaseRecordReport,
  partTimeTeacherStatusReport: apiService.report.partTimeTeacherStatus,
  userBalance: apiService.report.userBalance,
  staffLessonRecordReport: apiService.report.staffLessonRecordReport,
  staffClaimsReport: apiService.report.staffClaims,
  pettyCashSummaryReport: apiService.report.pettyCashSummaryReport,
  studentCourseHoursReport: apiService.report.studentCourseHoursReport,
  staffLeavesSummary: apiService.report.staffLeavesSummary,
  individualStaffAttendanceReport:
    apiService.report.individualStaffAttendanceReport,
};

const LoadingButton = ({ children, disabled, ...props }) => (
  <Button {...props} size="sm" disabled={disabled}>
    <small>
      <b>{disabled ? <Spinner size="sm" /> : children}</b>
    </small>
  </Button>
);

const ReportPage = () => {
  const [filterOptions, setFilterOptions] = useState(initialState);
  const [yearMonthFilterOptions, setYearMonthFilterOptions] = useState({
    year: new Date().getFullYear(),
    month: 1,
  });
  const [loading, setLoading] = useState(false);

  const { data: studentOptions, isLoading: loadingStudents } = useQuery(
    ["student"],
    () =>
      apiService.student
        .getAbsoluteMin(undefined, {
          show_deactivated: false,
        })
        .then((data) =>
          data.map((c) => ({
            value: c.id,
            label:
              c.firstName +
              " " +
              c.lastName +
              " (" +
              c?.centre?.shortName +
              ")",
            data: c,
          })),
        ),
  );

  const { data: staffOptions } = useQuery(
    ["teacher"],
    () =>
      apiService.staff
        .get(undefined, { show_deactivated: true })
        .then((staffs) =>
          staffs.map((staff) => ({
            value: staff.id,
            label: staff.fullName,
          })),
        ),
    { refetchOnWindowFocus: false },
  );

  const handleChange = (e) => {
    if (!moment(e.target.value, "YYYY-MM-DD", true).isValid()) return;
    return setFilterOptions((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const transformParams = () => {
    const date_from = new Date(filterOptions.date_from)
      .toISOString()
      .slice(0, 10);
    const date_to = new Date(filterOptions.date_to).toISOString().slice(0, 10);
    const staff_id = filterOptions.staff?.value;
    return {
      staff_id,
      date_from,
      date_to,
      grouped: filterOptions.grouped,
      ...yearMonthFilterOptions,
    };
  };

  const triggerDownload = (type) => {
    setLoading(true);
    let fileName = `report_${filterOptions.date_from}_${filterOptions.date_to}.csv`;
    if (type === "transaction")
      fileName = `transaction_${filterOptions.date_from}_${filterOptions.date_to}.xlsx`;
    else if (type === "ot" || type === "leave")
      fileName = filterOptions.grouped
        ? `${type}_${filterOptions.date_from}_${filterOptions.date_to}.zip`
        : `${type}_${filterOptions.staff ? filterOptions.staff?.label + "_" : ""}${filterOptions.date_from}_${filterOptions.date_to}.csv`;
    else if (type === "addPointRecord")
      fileName = `add_point_records_${filterOptions.date_from}_${filterOptions.date_to}.xlsx`;
    else if (type === "studentSummary")
      fileName = `student_summary_${yearMonthFilterOptions.year}-${
        months.find((month) => month.value == yearMonthFilterOptions.month)
          .label
      }.xlsx`;
    else if (type === "partTimeWageReport")
      fileName = `${filterOptions.date_from}_${filterOptions.date_to}_part_time_wage_resources.zip`;
    else if (type === "lessonRecordSummaryReport")
      fileName = `${filterOptions.date_from}_${filterOptions.date_to}_lesson_record_summary_report.xlsx`;
    else if (type === "staffAttendanceReport")
      fileName = `${filterOptions.date_from}_${filterOptions.date_to}_full_time_attendance_report.xlsx`;
    else if (type === "ludaAttendanceReport")
      fileName = `${filterOptions.date_from}_${filterOptions.date_to}_luda_staff_attendance_report.xlsx`;
    else if (type === "geniusAttendanceReport")
      fileName = `${filterOptions.date_from}_${filterOptions.date_to}_genius_staff_attendance_report.xlsx`;
    else if (type === "coursePurchaseRecordReport")
      fileName = `${filterOptions.date_from}_${filterOptions.date_to}_course_purchase_record_report.xlsx`;
    else if (type === "staffClaimsReport")
      fileName = `staff_claims_${yearMonthFilterOptions.year}-${
        months.find((month) => month.value == yearMonthFilterOptions.month)
          .label
      }.xlsx`;
    else if (type === "pettyCashSummaryReport")
      fileName = `petty_cash_claims_${yearMonthFilterOptions.year}-${
        months.find((month) => month.value == yearMonthFilterOptions.month)
          .label
      }.xlsx`;
    else if (type === "partTimeTeacherStatusReport")
      fileName = `PTT_Status_${new Date().toDateString()}.xlsx`;
    else if (type === "userBalance")
      fileName = `${filterOptions.date_to}_membership_balance.xlsx`;
    else if (type === "staffLessonRecordReport")
      fileName = `${filterOptions.date_from}_${filterOptions.date_to}_staff_lesson_record_report.xlsx`;
    else if (type === "studentCourseHoursReport")
      fileName = `${filterOptions.date_from}_${filterOptions.date_to}_student_course_hours_report.xlsx`;
    else if (type === "teachingHoursSummaryReport")
      fileName = `${filterOptions.date_from}_${filterOptions.date_to}_teaching_hours_summary_report.xlsx`;
    else if (type === "staffLeavesSummary")
      fileName = `${new Date().toISOString().split("T")[0]}_staff_leaves_summary_report.xlsx`;
    apiCallMapper[type](transformParams(), filterOptions.grouped)
      .then((data) => {
        fileResponseDownloader({ fileName, data });
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="content">
      <div className="row">
        <div className="col-md-6">
          <Card>
            <CardHeader>
              <h5 className="title">Staff Claims Application</h5>
            </CardHeader>
            <CardBody>
              <div className="row mb-1">
                <div className="col-12">
                  <InputGroup>
                    <div className="input-group-prepend">
                      <span className="input-group-text">Year</span>
                    </div>
                    <input
                      type="number"
                      min={2021}
                      max={3000}
                      className="form-control text-light"
                      onChange={(e) =>
                        setYearMonthFilterOptions((prev) => ({
                          ...prev,
                          year: e.target.value,
                        }))
                      }
                      value={yearMonthFilterOptions.year}
                    />
                  </InputGroup>
                </div>
                <div className="col-12">
                  <InputGroup>
                    <div className="input-group-prepend">
                      <span className="input-group-text">Month</span>
                    </div>
                    <select
                      value={yearMonthFilterOptions.month}
                      onChange={(e) =>
                        setYearMonthFilterOptions((prev) => ({
                          ...prev,
                          month: e.target.value,
                        }))
                      }
                      className="form form-control"
                    >
                      {months.map((option) => (
                        <option value={option.value} key={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </InputGroup>
                </div>
              </div>
              <div className="d-flex">
                <LoadingButton
                  color="info"
                  className="mr-2"
                  disabled={loading}
                  onClick={() => triggerDownload("staffClaimsReport")}
                >
                  Generate
                </LoadingButton>
              </div>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-6">
          <Card>
            <CardHeader>
              <h5 className="title">Petty Cash Claims</h5>
            </CardHeader>
            <CardBody>
              <div className="row mb-1">
                <div className="col-12">
                  <InputGroup>
                    <div className="input-group-prepend">
                      <span className="input-group-text">Year</span>
                    </div>
                    <input
                      type="number"
                      min={2021}
                      max={3000}
                      className="form-control text-light"
                      onChange={(e) =>
                        setYearMonthFilterOptions((prev) => ({
                          ...prev,
                          year: e.target.value,
                        }))
                      }
                      value={yearMonthFilterOptions.year}
                    />
                  </InputGroup>
                </div>
                <div className="col-12">
                  <InputGroup>
                    <div className="input-group-prepend">
                      <span className="input-group-text">Month</span>
                    </div>
                    <select
                      value={yearMonthFilterOptions.month}
                      onChange={(e) =>
                        setYearMonthFilterOptions((prev) => ({
                          ...prev,
                          month: e.target.value,
                        }))
                      }
                      className="form form-control"
                    >
                      {months.map((option) => (
                        <option value={option.value} key={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </InputGroup>
                </div>
              </div>
              <div className="d-flex">
                <LoadingButton
                  color="info"
                  className="mr-2"
                  disabled={loading}
                  onClick={() => triggerDownload("pettyCashSummaryReport")}
                >
                  Generate
                </LoadingButton>
              </div>
            </CardBody>
          </Card>
        </div>
        <ReportSelectionCard
          title="Course Purchase Records Summary"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          buttons={
            <>
              <LoadingButton
                color="info"
                className="mr-2"
                disabled={loading}
                onClick={() => triggerDownload("coursePurchaseRecordReport")}
              >
                Generate
              </LoadingButton>
            </>
          }
        />
        <ReportSelectionCard
          title="Teaching Hours Report"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          buttons={
            <>
              <LoadingButton
                color="info"
                className="mr-2"
                disabled={loading}
                onClick={() => triggerDownload("teachingHoursSummaryReport")}
              >
                Generate
              </LoadingButton>
            </>
          }
        />

        <ReportSelectionCard
          title="Coffee Shop Transactions"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          buttons={
            <LoadingButton
              color="info"
              className="mr-2"
              disabled={loading}
              onClick={() => triggerDownload("transaction")}
            >
              Generate
            </LoadingButton>
          }
        />

        <ReportSelectionCard
          title="Part Time Wage Report"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          buttons={
            <>
              <LoadingButton
                color="info"
                className="mr-2"
                disabled={loading}
                onClick={() => triggerDownload("partTimeWageReport")}
              >
                Generate
              </LoadingButton>
            </>
          }
        />
        <ReportSelectionCard
          title="Lesson Record Summary Report"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          buttons={
            <>
              <LoadingButton
                color="info"
                className="mr-2"
                disabled={loading}
                onClick={() => triggerDownload("lessonRecordSummaryReport")}
              >
                Generate
              </LoadingButton>
            </>
          }
        />
        <ReportSelectionCard
          title="Part Time Staff Status"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          buttons={
            <>
              <LoadingButton
                color="info"
                className="mr-2"
                disabled={loading}
                onClick={() => triggerDownload("partTimeTeacherStatusReport")}
              >
                Generate
              </LoadingButton>
            </>
          }
        />
        <ReportSelectionCard
          title="Staff Leave Summary"
          handleChange={handleChange}
          filterOptions={filterOptions}
          disable={{ date_from: true, date_to: true }}
          setFilterOptions={setFilterOptions}
          buttons={
            <>
              <LoadingButton
                color="info"
                className="mr-2"
                disabled={loading}
                onClick={() => triggerDownload("staffLeavesSummary")}
              >
                Generate
              </LoadingButton>
            </>
          }
        />
        <ReportSelectionCard
          title="Genius Staff Attendance"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          buttons={
            <>
              <LoadingButton
                color="info"
                className="mr-2"
                disabled={loading}
                onClick={() => triggerDownload("geniusAttendanceReport")}
              >
                Generate
              </LoadingButton>
            </>
          }
        />
        <ReportSelectionCard
          title="Staff Lesson Record Report"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          buttons={
            <LoadingButton
              color="info"
              className="mr-2"
              disabled={loading}
              onClick={() => triggerDownload("staffLessonRecordReport")}
            >
              Generate
            </LoadingButton>
          }
        />

        <ReportSelectionCard
          title="Membership Points Balance"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          disable={{ date_from: true }}
          buttons={
            <LoadingButton
              color="info"
              className="mr-2"
              disabled={loading}
              onClick={() => triggerDownload("userBalance")}
            >
              Generate
            </LoadingButton>
          }
        />

        <ReportSelectionCard
          title="Student Course Hours Report"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          disable={{ date_from: true }}
          additionalFilters={
            <Select
              className="react-select info mb-3"
              classNamePrefix="react-select"
              isLoading={loadingStudents}
              isClearable={true}
              onChange={(val) =>
                setFilterOptions((prev) => ({
                  ...prev,
                  student_id: val,
                }))
              }
              defaultValue={undefined}
              placeholder="Select student...."
              options={studentOptions || []}
            />
          }
          buttons={
            <>
              <LoadingButton
                color="info"
                className="mr-2"
                disabled={loading}
                onClick={() => triggerDownload("studentCourseHoursReport")}
              >
                Generate
              </LoadingButton>
            </>
          }
        />

        {/* <ReportSelectionCard */}
        {/*   title="Teaching Hours Summary Report" */}
        {/*   handleChange={handleChange} */}
        {/*   filterOptions={filterOptions} */}
        {/*   setFilterOptions={setFilterOptions} */}
        {/*   buttons={ */}
        {/*     <LoadingButton */}
        {/*       color="info" */}
        {/*       className="mr-2" */}
        {/*       disabled={loading} */}
        {/*       onClick={() => triggerDownload("weeklyTeachingHours")} */}
        {/*     > */}
        {/*       Generate */}
        {/*     </LoadingButton> */}
        {/*   } */}
        {/* /> */}

        <ReportSelectionCard
          title="Individual Staff Attendance Report"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          additionalFilters={
            <Select
              className="react-select info mb-3"
              classNamePrefix="react-select"
              isClearable={true}
              onChange={(val) =>
                setFilterOptions((prev) => ({
                  ...prev,
                  staff: val,
                }))
              }
              defaultValue={undefined}
              placeholder="Select staff...."
              options={staffOptions || []}
            />
          }
          buttons={
            <>
              <LoadingButton
                color="info"
                className="mr-2"
                disabled={loading}
                onClick={() =>
                  triggerDownload("individualStaffAttendanceReport")
                }
              >
                Download
              </LoadingButton>
            </>
          }
        />
        <ReportSelectionCard
          title="OT/Leave"
          handleChange={handleChange}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          additionalFilters={
            <Select
              className="react-select info mb-3"
              classNamePrefix="react-select"
              isClearable={true}
              onChange={(val) =>
                setFilterOptions((prev) => ({
                  ...prev,
                  staff: val,
                }))
              }
              defaultValue={undefined}
              placeholder="Select staff...."
              options={staffOptions || []}
            />
          }
          groupEnabled={true}
          groupButtonTitle="Download Leave/OT grouped by staff"
          buttons={
            <>
              <LoadingButton
                color="info"
                className="mr-2"
                disabled={loading}
                onClick={() => triggerDownload("leave")}
              >
                Generate Leave
              </LoadingButton>
              <LoadingButton
                color="info"
                className="mr-2"
                onClick={() => triggerDownload("ot")}
                disabled={loading}
              >
                Generate OT
              </LoadingButton>
            </>
          }
        />
      </div>
    </div>
  );
};

export default ReportPage;
